import * as React from "react";
import { useMemo } from "react";

import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { format } from "date-fns";

import { useAppSelector } from "app/hooks";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { Notification, NotificationTypes } from "services/types/notifications";
import { convertDateInTimezoneToCurrent } from "utils/common";

import {
  AppointmentNotification,
  CollectNotification,
  IntakeFormNotification,
  MessageNotification,
  ReportNotification,
  TaskNotification,
  TouchpointNotification
} from "./notifications-by-type";
import AmbientScribeNotificationComponent from "./notifications-by-type/ambient-scribe-notification.component";
import MentionNotification from "./notifications-by-type/mention-notification.component";
import NoahSyncedNotification from "./notifications-by-type/noah-synced-notification.component";

type NotificationItemProps = {
  notification: Notification;
  onDelete: (id: number) => void;
  readNotification: (id: number) => void;
};

export default function NotificationItemV2({
  notification,
  onDelete,
  readNotification
}: NotificationItemProps) {
  const { palette, shadows } = useTheme();

  const currentClinic = useAppSelector(selectCurrentClinic);

  const createdAtDateTime = useMemo(() => {
    const dateWithClinicTimeZone = convertDateInTimezoneToCurrent(
      notification.created_at,
      notification.clinic?.timezone ?? currentClinic?.timezone
    );

    return format(
      new Date(dateWithClinicTimeZone),
      "EEE MMM dd, yyyy hh:mm aaa"
    );
  }, [
    notification.created_at,
    notification.clinic?.timezone,
    currentClinic?.timezone
  ]);

  const { id, type, is_read } = notification;

  const NotificationBody = useMemo(() => {
    if (type === NotificationTypes.APPOINTMENT) {
      return <AppointmentNotification {...notification} />;
    }
    if (type === NotificationTypes.MESSAGE) {
      return <MessageNotification {...notification} />;
    }
    if (type === NotificationTypes.REPORT) {
      return <ReportNotification {...notification} />;
    }
    if (type === NotificationTypes.INTAKE_FORM) {
      return <IntakeFormNotification {...notification} />;
    }
    if (type === NotificationTypes.TASK) {
      return <TaskNotification {...notification} />;
    }
    if (type === NotificationTypes.NOAH_HISTORY_SYNCED) {
      return <NoahSyncedNotification {...notification} />;
    }
    if (type === NotificationTypes.TOUCHPOINT) {
      return <TouchpointNotification {...notification} />;
    }
    if (type === NotificationTypes.AMBIENT_SCRIBE) {
      return <AmbientScribeNotificationComponent {...notification} />;
    }
    if (type === NotificationTypes.COLLECTION) {
      return <CollectNotification {...notification} />;
    }
    if (type === NotificationTypes.MENTION) {
      return <MentionNotification {...notification} />;
    }
  }, [notification, type]);

  return (
    <ListItem
      key={id}
      sx={{
        position: "relative",
        "&:hover .delete-button": {
          display: "flex"
        },
        cursor: is_read ? undefined : "pointer",
        background: is_read ? undefined : palette.actionBar.bgcolor,
        border: is_read
          ? `1px solid ${palette.neutral[300]}`
          : `1px solid ${palette.neutral[100]}`,
        borderRadius: "6px",
        boxShadow: is_read ? undefined : shadows[2]
      }}
      onClick={() => {
        if (!is_read) {
          readNotification(id);
        }
      }}
    >
      <Stack width="100%">
        {NotificationBody}
        <Typography ml="auto" color={grey[800]}>
          {createdAtDateTime}
        </Typography>
      </Stack>
      <IconButton
        className="delete-button"
        sx={{
          display: "none",
          position: "absolute",
          right: 10
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.(id);
        }}
      >
        <DeleteOutlinedIcon color="action" />
      </IconButton>
    </ListItem>
  );
}
